import { createApi } from '@reduxjs/toolkit/query/react';
import { reportDatasetRows } from './reportDatasetRows';

import { overrideFetchBaseQuery } from '../utils/overrideFetchBaseQuery';
import { baseQueryWithZodValidation } from '../utils/baseQueryWithZodValidation';
import { CUBE_CARD_IDS } from './cardIds';
import { CUBE_CARD_PARAM_IDS } from './cardParamIds';
import { BrandWiseMTOPendencySchema, MTOHomepageOneViewSchema } from './schemas';
import type { BrandWiseMTOPendencyResponse, MTOHomepageOneViewResponse } from './schemas';
import { hubStoreImageQueueRequest } from './hubStoreImageQueue';
import type { HubStoreImageQueueRow } from './hubStoreImageQueue';
import {
  InwardTrackerBrandSummarySchema,
  InwardTrackerItemSchema,
  InwardTrackerStoreBreakdownSchema,
} from './inwardTrackerSchemas';
import { SaleStockBrandSummary, SaleStockBrandSummarySchema } from './saleStockSchemas';
import {
  DepthBreadthBrandOptions,
  DepthBreadthBrandOptionsSchema,
  DepthBreadthDaily,
  DepthBreadthDailySchema,
  DepthBreadthLive,
  DepthBreadthLiveSchema,
  DepthBreadthTargets,
  DepthBreadthTargetsSchema,
} from './depthBreadthSchemas';
import {
  BrandMtdNetGmv,
  BrandMtdNetGmvSchema,
  StoreBrandSummary,
  StoreBrandSummarySchema,
  StyleStoreSplit,
  StyleStoreSplitSchema,
} from './storeBrandSchemas';
import {
  AiTagWeeklySummary,
  AiTagWeeklySummarySchema,
  BestsellersSizeDemand,
  BestsellersSizeDemandSchema,
  BestsellersTopStyles,
  BestsellersTopStylesSchema,
  CoyuSearches,
  CoyuSearchesSchema,
  TagDetailItems,
  TagDetailItemsSchema,
} from './bestsellersTrendsSchemas';
import {
  StockTransferBrandSummary,
  StockTransferBrandSummarySchema,
  StockTransferGpoList,
  StockTransferGpoListSchema,
} from './stockTransferSchemas';
import {
  OpsDcTimeline,
  OpsDcTimelineSchema,
  OpsGpoGatepassTimeline,
  OpsGpoGatepassTimelineSchema,
  OpsGpo,
  OpsGpoSchema,
  OpsInwardsDc,
  OpsInwardsDcSchema,
  OpsPendingDc,
  OpsPendingDcSchema,
  OpsStockAgeing,
  OpsStockAgeingSchema,
  OpsStockLocation,
  OpsActionMatrix,
  OpsActionMatrixSchema,
  OpsSkuExport,
  OpsSkuExportSchema,
  OpsStockLocationExtras,
  OpsStockLocationExtrasSchema,
  OpsStockLocationSchema,
  OpsStockRecon,
  OpsStockReconSchema,
  OpsStockVelocity,
  OpsStockVelocitySchema,
} from './opsSchemas';
import {
  MtoOrdersSummary,
  MtoOrdersSummarySchema,
  MtoLiveOrders,
  MtoLiveOrdersSchema,
  MtoWeeklyTrend,
  MtoWeeklyTrendSchema,
  MtoDelayHistogram,
  MtoDelayHistogramSchema,
  MtoDelayDetailOrders,
  MtoDelayDetailOrdersSchema,
  MtoLiveOrdersDetail,
  MtoLiveOrdersDetailSchema,
  MtoMerchHeadBrandContribution,
  MtoMerchHeadBrandContributionSchema,
  MtoMerchHeadIssueBreakdown,
  MtoMerchHeadIssueBreakdownSchema,
  MtoMerchOwnerWindowComparison,
  MtoMerchOwnerWindowComparisonSchema,
  MtoMerchHeadRawIssueLines,
  MtoMerchHeadRawIssueLinesSchema,
  MtoMerchHeadWeeklyLeaderboard,
  MtoMerchHeadWeeklyLeaderboardSchema,
  MtoMerchHeadWeeklyTrend,
  MtoMerchHeadWeeklyTrendSchema,
} from './mtoHealthSchemas';
import {
  JitB2B2CVendorPackingSlaByBrand,
  JitB2B2CVendorPackingSlaByBrandSchema,
  JitB2B2CVendorPackingSlaRaw,
  JitB2B2CVendorPackingSlaRawSchema,
  JitB2B2CVendorPackingSlaSummary,
  JitB2B2CVendorPackingSlaSummarySchema,
  JitB2B2CVendorPackingSlaWeekly,
  JitB2B2CVendorPackingSlaWeeklySchema,
} from './jitB2B2CSchemas';
import {
  CatalogCreation,
  CatalogCreationSchema,
  CatalogQuality,
  CatalogQualitySchema,
  CatalogStylesHealth,
  CatalogStylesHealthSchema,
} from './catalogingHealthSchemas';
import {
  StockAgeingBrandBucket,
  StockAgeingBrandBucketSchema,
  StockAgeingFacilityBucket,
  StockAgeingFacilityBucketSchema,
  StockAgeingWritedown,
  StockAgeingWritedownSchema,
} from './stockAgeingSchemas';
import {
  OnlineAddToCarts,
  OnlineAddToCartsSchema,
  OnlineLiveStyles,
  OnlineLiveStylesSchema,
  OnlinePerformanceDaily,
  OnlinePerformanceDailySchema,
  OnlinePerformanceMerchDashMix,
  OnlinePerformanceMerchDashMixSchema,
  OnlinePerformanceStyleLevel,
  OnlinePerformanceStyleLevelSchema,
  OnlineTopTrafficStyles,
  OnlineTopTrafficStylesSchema,
  OnlineTrafficBrandSource,
  OnlineTrafficBrandSourceSchema,
} from './onlinePerformanceSchemas';
import {
  StockReconciliationFacility,
  StockReconciliationFacilitySchema,
} from './stockReconciliationSchemas';
import {
  MonthlyKpiBrandManagerTarget,
  MonthlyKpiBrandManagerTargetSchema,
  MonthlyKpiCx,
  MonthlyKpiCxSchema,
  MonthlyKpiRevenue,
  MonthlyKpiRevenueSchema,
  MonthlyKpiSizeAvailability,
  MonthlyKpiSizeAvailabilitySchema,
} from './monthlyMerchKpiSchemas';
import { AuditHistory, AuditHistorySchema } from './auditHistorySchemas';
import {
  InventoryShelves,
  InventoryShelvesSchema,
  InventoryStockHistory,
  InventoryStockHistorySchema,
  InventoryCartBlocks,
  InventoryCartBlocksSchema,
} from './inventoryDetailsSchemas';
import { StylistLeaderboard, StylistLeaderboardSchema } from './stylistLeaderboardSchemas';
import { z, type ZodSchema } from 'zod';
import {
  OpenWorkMapFacilityRow,
  OpenWorkMapFacilityRowsSchema,
  OpenWorkMapRiderRow,
  OpenWorkMapRiderRowsSchema,
  OpenWorkMapQueryScope,
  OpenWorkMapWorkRow,
  OpenWorkMapWorkRowsSchema,
} from './openWorkMapSchemas';
import { openWorkMapCardUrl } from './openWorkMapCardUrl';
import {
  RiderDayDistanceBatch,
  RiderDayDistanceQuery,
  RiderDayDistanceRowsSchema,
  RiderDayEvidenceRow,
  RiderDayEvidenceRowsSchema,
  RiderDayFailedAttemptRow,
  RiderDayFailedAttemptRowsSchema,
  RiderDayQuery,
  RiderDayShiftRow,
  RiderDayShiftRowsSchema,
  RiderDayTripBundle,
  RiderDayTripJobRowsSchema,
  RiderFailedAttempts7dRow,
  RiderFailedAttempts7dRowsSchema,
  RiderDayTripLegRowsSchema,
  RiderDayTripRowsSchema,
  RiderDayTripSegmentRowsSchema,
  RiderDayTripSummaryRowsSchema,
} from './riderDaySchemas';

/* Defensive parser — schema mismatches log a warning and pass the raw response
   through. Prevents one bad row from blanking the entire tab. */
const safeParse =
  <T>(schema: ZodSchema, label: string) =>
  (response: unknown): T => {
    const result = schema.safeParse(response);
    if (result.success) return result.data as T;
    // eslint-disable-next-line no-console
    console.warn(
      `[metabaseApi] ${label} schema mismatch — passing raw response through:`,
      result.error?.issues?.slice(0, 5),
    );
    return response as T;
  };

const metabaseTextParam = (name: string, value: string) => ({
  type: 'category',
  value,
  target: ['variable', ['template-tag', name]],
});

const metabaseDateRangeParam = (value: string) => ({
  type: 'date/range',
  value,
  target: ['dimension', ['template-tag', 'date_range']],
});

const metabaseBrandNameParams = (brands: string[] = []) =>
  brands.length > 0
    ? [{ type: 'category', value: brands, target: ['variable', ['template-tag', 'brand_name']] }]
    : [];

const metabaseCategoryListParams = (name: string, values: string[] = []) =>
  values.length > 0
    ? [{ type: 'category', value: values, target: ['variable', ['template-tag', name]] }]
    : [];
import type {
  InwardTrackerBrandSummary,
  InwardTrackerItem,
  InwardTrackerStoreBreakdown,
} from './inwardTrackerSchemas';

/* Metabase's card query API (pMBQL `stages` cards) requires each `parameters[]`
   object to carry a non-blank `id`, AND binds the value to a template-tag by
   matching that `id` to the tag's stored parameter id — not by the `target`/tag
   name. A missing id 400s on schema ("missing required key, received: nil"); a
   present-but-wrong id leaves the tag unfilled ("missing required parameters").
   So we look up the real per-card id from CUBE_CARD_PARAM_IDS (keyed by card id
   in the request URL, then by the tag name in the parameter's target) and stamp
   it onto every parameter that doesn't already carry one. */
const templateTagNameFromTarget = (target: unknown): string | null => {
  if (Array.isArray(target)) {
    const tag = target.find(part => Array.isArray(part) && part[0] === 'template-tag') as
      | [string, string]
      | undefined;
    if (tag && typeof tag[1] === 'string' && tag[1].length > 0) return tag[1];
  }
  return null;
};

const cardIdFromUrl = (url: unknown): number | null => {
  if (typeof url !== 'string') return null;
  const m = url.match(/\/card\/(\d+)\/query\/json/);
  return m ? Number(m[1]) : null;
};

const withMetabaseParameterIds =
  (
    baseQuery: ReturnType<typeof overrideFetchBaseQuery>,
  ): ReturnType<typeof overrideFetchBaseQuery> =>
  (args, api, extraOptions) => {
    if (args && typeof args !== 'string' && args.body && Array.isArray(args.body.parameters)) {
      const cardId = cardIdFromUrl(args.url);
      const tagIds = cardId != null ? CUBE_CARD_PARAM_IDS[cardId] : undefined;
      const parameters = args.body.parameters.map((p: Record<string, unknown>, i: number) => {
        if (!p || typeof p !== 'object' || ('id' in p && p.id)) return p;
        const name = templateTagNameFromTarget(p.target);
        const mapped = name && tagIds ? tagIds[name] : undefined;
        if (name && !mapped) {
          // eslint-disable-next-line no-console
          console.warn(
            `[metabaseApi] no mapped parameter id for card ${cardId} tag "${name}" — ` +
              `add it to CUBE_CARD_PARAM_IDS; falling back to the tag name (may not bind).`,
          );
        }
        return { ...p, id: mapped || name || `param_${i}` };
      });
      return baseQuery({ ...args, body: { ...args.body, parameters } }, api, extraOptions);
    }
    return baseQuery(args, api, extraOptions);
  };

export const metabaseApi = createApi({
  reducerPath: 'metabaseApi',
  baseQuery: baseQueryWithZodValidation(withMetabaseParameterIds(overrideFetchBaseQuery({})), {
    passThroughChecks: true,
  }),
  endpoints: builder => ({
    openWorkMapWork: builder.query<Array<OpenWorkMapWorkRow>, OpenWorkMapQueryScope>({
      query: ({ facilityIds }) => ({
        method: 'POST',
        url: openWorkMapCardUrl(CUBE_CARD_IDS.openWorkMapWork),
        body: {
          parameters: [metabaseTextParam('facility_ids', facilityIds.join(','))],
        },
      }),
      transformResponse: response => OpenWorkMapWorkRowsSchema.parse(response),
      keepUnusedDataFor: 60,
    }),
    openWorkMapActiveRiders: builder.query<Array<OpenWorkMapRiderRow>, OpenWorkMapQueryScope>({
      query: ({ facilityIds }) => ({
        method: 'POST',
        url: openWorkMapCardUrl(CUBE_CARD_IDS.openWorkMapActiveRiders),
        body: {
          parameters: [metabaseTextParam('facility_ids', facilityIds.join(','))],
        },
      }),
      transformResponse: response => OpenWorkMapRiderRowsSchema.parse(response),
      keepUnusedDataFor: 60,
    }),
    openWorkMapActiveFacilities: builder.query<Array<OpenWorkMapFacilityRow>, void>({
      query: () => ({
        method: 'POST',
        url: openWorkMapCardUrl(CUBE_CARD_IDS.openWorkMapActiveFacilities),
      }),
      transformResponse: response => OpenWorkMapFacilityRowsSchema.parse(response),
      keepUnusedDataFor: 300,
    }),
    openWorkMapJitExpressFacilities: builder.query<Array<OpenWorkMapFacilityRow>, void>({
      query: () => ({
        method: 'POST',
        url: openWorkMapCardUrl(CUBE_CARD_IDS.openWorkMapJitExpressFacilities),
      }),
      transformResponse: response => OpenWorkMapFacilityRowsSchema.parse(response),
      keepUnusedDataFor: 300,
    }),
    riderDayEvidence: builder.query<Array<RiderDayEvidenceRow>, RiderDayQuery>({
      query: ({ riderId, businessDate }) => ({
        method: 'POST',
        url: openWorkMapCardUrl(CUBE_CARD_IDS.riderDayEvidence),
        body: {
          parameters: [
            metabaseTextParam('rider_id', String(riderId)),
            metabaseTextParam('business_date', businessDate),
          ],
        },
      }),
      transformResponse: response => RiderDayEvidenceRowsSchema.parse(response),
      keepUnusedDataFor: 300,
    }),
    riderDayShiftContext: builder.query<Array<RiderDayShiftRow>, RiderDayQuery>({
      query: ({ riderId, businessDate }) => ({
        method: 'POST',
        url: openWorkMapCardUrl(CUBE_CARD_IDS.riderDayShiftContext),
        body: {
          parameters: [
            metabaseTextParam('rider_id', String(riderId)),
            metabaseTextParam('business_date', businessDate),
          ],
        },
      }),
      transformResponse: response => RiderDayShiftRowsSchema.parse(response),
      keepUnusedDataFor: 300,
    }),
    riderDayDistanceEvidence: builder.query<RiderDayDistanceBatch, RiderDayDistanceQuery>({
      async queryFn({ riderId, businessDate, shifts }, _queryApi, _extraOptions, baseQuery) {
        const results = await Promise.all(
          shifts.map(async shift => {
            const response = await baseQuery({
              method: 'POST',
              url: openWorkMapCardUrl(CUBE_CARD_IDS.riderDayDistanceEvidence),
              body: {
                parameters: [
                  metabaseTextParam('rider_id', String(riderId)),
                  metabaseTextParam('business_date', businessDate),
                  metabaseTextParam('shift_id', String(shift.shift_id)),
                  metabaseTextParam('shift_started_at', shift.shift_started_at),
                  metabaseTextParam('shift_ended_at', shift.distance_window_ended_at),
                ],
              },
            });
            if (response.error) return { shiftId: shift.shift_id, row: null };
            const parsed = RiderDayDistanceRowsSchema.safeParse(response.data);
            return {
              shiftId: shift.shift_id,
              row: parsed.success ? (parsed.data[0] ?? null) : null,
            };
          }),
        );
        return {
          data: {
            rows: results.flatMap(result => (result.row ? [result.row] : [])),
            failedShiftIds: results.filter(result => !result.row).map(result => result.shiftId),
          },
        };
      },
      keepUnusedDataFor: 300,
    }),
    /* Trip distance (rider-trip-distance-v6): five one-rider/one-day cards
       fetched together so the summary, trips, legs, jobs and map always
       describe the same materialisation. A contract mismatch fails the whole
       bundle rather than showing a partial breakdown. */
    riderDayTrips: builder.query<RiderDayTripBundle, RiderDayQuery>({
      async queryFn({ riderId, businessDate }, _queryApi, _extraOptions, baseQuery) {
        const parameters = [
          metabaseTextParam('rider_id', String(riderId)),
          metabaseTextParam('business_date', businessDate),
        ];
        const fetchCard = (cardId: number) =>
          baseQuery({ method: 'POST', url: openWorkMapCardUrl(cardId), body: { parameters } });
        const [summary, trips, legs, jobs, segments] = await Promise.all([
          fetchCard(CUBE_CARD_IDS.riderDayTripSummary),
          fetchCard(CUBE_CARD_IDS.riderDayTrips),
          fetchCard(CUBE_CARD_IDS.riderDayTripLegs),
          fetchCard(CUBE_CARD_IDS.riderDayTripJobs),
          fetchCard(CUBE_CARD_IDS.riderDayTripSegments),
        ]);
        const failed = [summary, trips, legs, jobs, segments].find(result => result.error);
        if (failed?.error) return { error: failed.error };
        try {
          return {
            data: {
              summary: RiderDayTripSummaryRowsSchema.parse(summary.data)[0] ?? null,
              trips: RiderDayTripRowsSchema.parse(trips.data),
              legs: RiderDayTripLegRowsSchema.parse(legs.data),
              jobs: RiderDayTripJobRowsSchema.parse(jobs.data),
              segments: RiderDayTripSegmentRowsSchema.parse(segments.data),
            },
          };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: error instanceof Error ? error.message : 'Rider trip contract mismatch',
            },
          };
        }
      },
      keepUnusedDataFor: 300,
    }),
    /* Failed deliveries / return pickups marked on one Rider Day, with the
       evidence and map points (mesh mart_rider_failed_attempt_evidence).
       Separate from the trip bundle: a failure here must not hide the distance. */
    riderDayFailedAttempts: builder.query<Array<RiderDayFailedAttemptRow>, RiderDayQuery>({
      query: ({ riderId, businessDate }) => ({
        method: 'POST',
        url: openWorkMapCardUrl(CUBE_CARD_IDS.riderDayFailedAttempts),
        body: {
          parameters: [
            metabaseTextParam('rider_id', String(riderId)),
            metabaseTextParam('business_date', businessDate),
          ],
        },
      }),
      transformResponse: response => RiderDayFailedAttemptRowsSchema.parse(response),
      keepUnusedDataFor: 300,
    }),
    /* Riders with failed attempts showing no sign of a visit in the last 7 days
       (mesh mart_rider_failed_attempt_evidence), for the Rider Control Centre. */
    riderFailedAttempts7d: builder.query<Array<RiderFailedAttempts7dRow>, void>({
      query: () => ({
        method: 'POST',
        url: openWorkMapCardUrl(CUBE_CARD_IDS.riderFailedAttempts7d),
      }),
      transformResponse: response => RiderFailedAttempts7dRowsSchema.parse(response),
      keepUnusedDataFor: 300,
    }),
    hubStoreImageQueue: builder.query<Array<HubStoreImageQueueRow>, { facilityId: number }>({
      query: hubStoreImageQueueRequest,
      keepUnusedDataFor: 60,
    }),
    // Ad-hoc read-only ClickHouse query (single SELECT/WITH). Routes through the
    // gateway /cube/dataset passthrough (auth + metabase key injected server-side;
    // role-restricted to reports). Returns rows as objects keyed by column name.
    runReportQuery: builder.query<
      Array<Record<string, unknown>>,
      { sql: string; requireCompleted?: boolean }
    >({
      query: ({ sql }) => ({
        method: 'POST',
        url: '/cube/dataset',
        body: { database: 3, type: 'native', native: { query: sql } },
      }),
      transformResponse: (response: unknown, _meta, arg) =>
        reportDatasetRows(response, arg.requireCompleted),
      keepUnusedDataFor: 300,
    }),
    /** Run a saved Cube card (no parameters) and return its rows as objects. */
    runCard: builder.query<Array<Record<string, unknown>>, { cardId: number }>({
      query: ({ cardId }) => ({
        method: 'POST',
        url: `/cube/card/${cardId}/query/json`,
      }),
      transformResponse: (response: unknown) =>
        Array.isArray(response) ? (response as Array<Record<string, unknown>>) : [],
      keepUnusedDataFor: 300,
    }),
    brandWiseMTOPendency: builder.query<BrandWiseMTOPendencyResponse, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.brandWiseMTOPendency}/query/json`,
      }),
      transformResponse: (response: unknown) => {
        return BrandWiseMTOPendencySchema.parse(response);
      },
      keepUnusedDataFor: 600,
    }),
    mtoHomepageOneView: builder.query<
      MTOHomepageOneViewResponse,
      { cardId: number; vendorId?: number }
    >({
      query: ({ cardId, vendorId }) => ({
        method: 'POST',
        url: `/cube/mto-homepage-one-view/card/${cardId}/query/json`,
        headers: vendorId
          ? {
              'x-lk-vendorid-for-admin': String(vendorId),
            }
          : undefined,
        body: vendorId
          ? {
              ignore_cache: false,
              collection_preview: false,
              parameters: [
                {
                  type: 'number/=',
                  value: [vendorId],
                  target: ['variable', ['template-tag', 'vendor_id']],
                },
              ],
            }
          : undefined,
      }),
      transformResponse: (response: unknown) => {
        return MTOHomepageOneViewSchema.parse(response);
      },
      keepUnusedDataFor: 600,
    }),
    inwardTrackerBrandSummary: builder.query<InwardTrackerBrandSummary, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.inwardTrackerBrandSummary}/query/json`,
      }),
      transformResponse: (response: unknown) => {
        return InwardTrackerBrandSummarySchema.parse(response);
      },
      keepUnusedDataFor: 600,
    }),
    inwardTrackerItems: builder.query<InwardTrackerItem, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.inwardTrackerItems}/query/json`,
      }),
      transformResponse: (response: unknown) => {
        return InwardTrackerItemSchema.parse(response);
      },
      keepUnusedDataFor: 600,
    }),
    inwardTrackerStoreBreakdown: builder.query<InwardTrackerStoreBreakdown, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.inwardTrackerStoreBreakdown}/query/json`,
      }),
      transformResponse: (response: unknown) => {
        return InwardTrackerStoreBreakdownSchema.parse(response);
      },
      keepUnusedDataFor: 600,
    }),
    saleStockBrandSummary: builder.query<
      SaleStockBrandSummary,
      { dateStart: string; dateEnd: string }
    >({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.saleStockBrandSummary}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
          ],
        },
      }),
      transformResponse: safeParse<SaleStockBrandSummary>(
        SaleStockBrandSummarySchema,
        'saleStockBrandSummary',
      ),
      keepUnusedDataFor: 600,
    }),
    storeBrandSummary: builder.query<
      StoreBrandSummary,
      { dateStart: string; dateEnd: string; brands?: string[] }
    >({
      query: ({ dateStart, dateEnd, brands = [] }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.storeBrandSummary}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
            ...(brands.length > 0
              ? [
                  {
                    type: 'category',
                    value: brands.join('|||'),
                    target: ['variable', ['template-tag', 'brand_names']],
                  },
                ]
              : []),
          ],
        },
      }),
      transformResponse: safeParse<StoreBrandSummary>(StoreBrandSummarySchema, 'storeBrandSummary'),
      keepUnusedDataFor: 600,
    }),
    brandMtdNetGmv: builder.query<BrandMtdNetGmv, { dateStart: string; dateEnd: string }>({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.brandMtdNetGmv}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
          ],
        },
      }),
      transformResponse: safeParse<BrandMtdNetGmv>(BrandMtdNetGmvSchema, 'brandMtdNetGmv'),
      keepUnusedDataFor: 600,
    }),
    allStylesSoldStoreSplit: builder.query<StyleStoreSplit, { dateStart: string; dateEnd: string }>(
      {
        query: ({ dateStart, dateEnd }) => ({
          method: 'POST',
          url: `/cube/card/${CUBE_CARD_IDS.allStylesSoldStoreSplit}/query/json`,
          body: {
            parameters: [
              {
                type: 'category',
                value: dateStart,
                target: ['variable', ['template-tag', 'date_start']],
              },
              {
                type: 'category',
                value: dateEnd,
                target: ['variable', ['template-tag', 'date_end']],
              },
            ],
          },
        }),
        transformResponse: safeParse<StyleStoreSplit>(
          StyleStoreSplitSchema,
          'allStylesSoldStoreSplit',
        ),
        keepUnusedDataFor: 600,
      },
    ),
    bestsellersTopStyles: builder.query<
      BestsellersTopStyles,
      { dateStart: string; dateEnd: string }
    >({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.bestsellersTopStyles}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
          ],
        },
      }),
      transformResponse: safeParse<BestsellersTopStyles>(
        BestsellersTopStylesSchema,
        'bestsellersTopStyles',
      ),
      keepUnusedDataFor: 600,
    }),
    bestsellersSizeDemand: builder.query<
      BestsellersSizeDemand,
      { dateStart: string; dateEnd: string }
    >({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.bestsellersSizeDemand}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
          ],
        },
      }),
      transformResponse: safeParse<BestsellersSizeDemand>(
        BestsellersSizeDemandSchema,
        'bestsellersSizeDemand',
      ),
      keepUnusedDataFor: 600,
    }),
    aiTagWeeklySummary: builder.query<AiTagWeeklySummary, { dateStart: string; dateEnd: string }>({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.aiTagWeeklySummary}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
          ],
        },
      }),
      transformResponse: safeParse<AiTagWeeklySummary>(
        AiTagWeeklySummarySchema,
        'aiTagWeeklySummary',
      ),
      keepUnusedDataFor: 600,
    }),
    coyuSearchesTopQueries: builder.query<CoyuSearches, { dateStart: string; dateEnd: string }>({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.coyuSearchesTopQueries}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
          ],
        },
      }),
      transformResponse: safeParse<CoyuSearches>(CoyuSearchesSchema, 'coyuSearchesTopQueries'),
      keepUnusedDataFor: 600,
    }),
    stockTransferBrandSummary: builder.query<
      StockTransferBrandSummary,
      { dateStart: string; dateEnd: string }
    >({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.stockTransferBrandSummary}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
          ],
        },
      }),
      transformResponse: safeParse<StockTransferBrandSummary>(
        StockTransferBrandSummarySchema,
        'stockTransferBrandSummary',
      ),
      keepUnusedDataFor: 600,
    }),
    stockTransferGpoList: builder.query<
      StockTransferGpoList,
      {
        dateStart: string;
        dateEnd: string;
        brands?: string[];
        brandManagers?: string[];
        merchPortfolios?: string[];
        stores?: string[];
      }
    >({
      query: ({
        dateStart,
        dateEnd,
        brands = [],
        brandManagers = [],
        merchPortfolios = [],
        stores = [],
      }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.stockTransferGpoList}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
            ...(brands.length > 0
              ? [
                  {
                    type: 'category',
                    value: brands.join('|||'),
                    target: ['variable', ['template-tag', 'brand_names']],
                  },
                ]
              : []),
            ...(brandManagers.length > 0
              ? [
                  {
                    type: 'category',
                    value: brandManagers.join('|||'),
                    target: ['variable', ['template-tag', 'brand_managers']],
                  },
                ]
              : []),
            ...(merchPortfolios.length > 0
              ? [
                  {
                    type: 'category',
                    value: merchPortfolios.join('|||'),
                    target: ['variable', ['template-tag', 'merch_portfolios']],
                  },
                ]
              : []),
            ...(stores.length > 0
              ? [
                  {
                    type: 'category',
                    value: stores.join('|||'),
                    target: ['variable', ['template-tag', 'store_names']],
                  },
                ]
              : []),
          ],
        },
      }),
      transformResponse: safeParse<StockTransferGpoList>(
        StockTransferGpoListSchema,
        'stockTransferGpoList',
      ),
      keepUnusedDataFor: 600,
    }),
    mtoOrdersSummary: builder.query<MtoOrdersSummary, { dateStart: string; dateEnd: string }>({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.mtoOrdersSummary}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
          ],
        },
      }),
      transformResponse: safeParse<MtoOrdersSummary>(MtoOrdersSummarySchema, 'mtoOrdersSummary'),
      keepUnusedDataFor: 600,
    }),
    mtoLiveOrders: builder.query<MtoLiveOrders, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.mtoLiveOrders}/query/json`,
      }),
      transformResponse: safeParse<MtoLiveOrders>(MtoLiveOrdersSchema, 'mtoLiveOrders'),
      keepUnusedDataFor: 600,
    }),
    mtoWeeklyTrend: builder.query<MtoWeeklyTrend, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.mtoWeeklyTrend}/query/json`,
      }),
      transformResponse: safeParse<MtoWeeklyTrend>(MtoWeeklyTrendSchema, 'mtoWeeklyTrend'),
      keepUnusedDataFor: 600,
    }),
    mtoDelayHistogram: builder.query<MtoDelayHistogram, { dateStart: string; dateEnd: string }>({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.mtoDelayHistogram}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
          ],
        },
      }),
      transformResponse: safeParse<MtoDelayHistogram>(MtoDelayHistogramSchema, 'mtoDelayHistogram'),
      keepUnusedDataFor: 600,
    }),
    mtoDelayDetailOrders: builder.query<
      MtoDelayDetailOrders,
      { dateStart: string; dateEnd: string }
    >({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.mtoDelayDetailOrders}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
          ],
        },
      }),
      transformResponse: safeParse<MtoDelayDetailOrders>(
        MtoDelayDetailOrdersSchema,
        'mtoDelayDetailOrders',
      ),
      keepUnusedDataFor: 600,
    }),
    mtoLiveOrdersDetail: builder.query<MtoLiveOrdersDetail, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.mtoLiveOrdersDetail}/query/json`,
      }),
      transformResponse: safeParse<MtoLiveOrdersDetail>(
        MtoLiveOrdersDetailSchema,
        'mtoLiveOrdersDetail',
      ),
      keepUnusedDataFor: 600,
    }),
    mtoMerchHeadWeeklyLeaderboard: builder.query<
      MtoMerchHeadWeeklyLeaderboard,
      { dateRange: string; brands?: string[] }
    >({
      query: ({ dateRange, brands = [] }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.mtoMerchHeadWeeklyLeaderboard}/query/json`,
        body: {
          parameters: [metabaseDateRangeParam(dateRange), ...metabaseBrandNameParams(brands)],
        },
      }),
      transformResponse: safeParse<MtoMerchHeadWeeklyLeaderboard>(
        MtoMerchHeadWeeklyLeaderboardSchema,
        'mtoMerchHeadWeeklyLeaderboard',
      ),
      keepUnusedDataFor: 600,
    }),
    mtoMerchHeadSelectedWindowComparison: builder.query<
      MtoMerchOwnerWindowComparison,
      {
        dateStart: string;
        dateEnd: string;
        brands?: string[];
        brandManagers?: string[];
        merchPortfolios?: string[];
      }
    >({
      query: ({ dateStart, dateEnd, brands = [], brandManagers = [], merchPortfolios = [] }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.mtoMerchHeadSelectedWindowComparison}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
            ...metabaseBrandNameParams(brands),
            ...metabaseCategoryListParams('brand_manager', brandManagers),
            ...metabaseCategoryListParams('merch_portfolio', merchPortfolios),
          ],
        },
      }),
      transformResponse: safeParse<MtoMerchOwnerWindowComparison>(
        MtoMerchOwnerWindowComparisonSchema,
        'mtoMerchHeadSelectedWindowComparison',
      ),
      keepUnusedDataFor: 600,
    }),
    mtoMerchPortfolioSelectedWindowComparison: builder.query<
      MtoMerchOwnerWindowComparison,
      {
        dateStart: string;
        dateEnd: string;
        brands?: string[];
        brandManagers?: string[];
        merchPortfolios?: string[];
      }
    >({
      query: ({ dateStart, dateEnd, brands = [], brandManagers = [], merchPortfolios = [] }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.mtoMerchPortfolioSelectedWindowComparison}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
            ...metabaseBrandNameParams(brands),
            ...metabaseCategoryListParams('brand_manager', brandManagers),
            ...metabaseCategoryListParams('merch_portfolio', merchPortfolios),
          ],
        },
      }),
      transformResponse: safeParse<MtoMerchOwnerWindowComparison>(
        MtoMerchOwnerWindowComparisonSchema,
        'mtoMerchPortfolioSelectedWindowComparison',
      ),
      keepUnusedDataFor: 600,
    }),
    mtoMerchHeadWeeklyTrend: builder.query<
      MtoMerchHeadWeeklyTrend,
      { dateRange: string; brands?: string[] }
    >({
      query: ({ dateRange, brands = [] }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.mtoMerchHeadWeeklyTrend}/query/json`,
        body: {
          parameters: [metabaseDateRangeParam(dateRange), ...metabaseBrandNameParams(brands)],
        },
      }),
      transformResponse: safeParse<MtoMerchHeadWeeklyTrend>(
        MtoMerchHeadWeeklyTrendSchema,
        'mtoMerchHeadWeeklyTrend',
      ),
      keepUnusedDataFor: 600,
    }),
    mtoMerchHeadBrandContribution: builder.query<
      MtoMerchHeadBrandContribution,
      { dateRange: string; brands?: string[] }
    >({
      query: ({ dateRange, brands = [] }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.mtoMerchHeadBrandContribution}/query/json`,
        body: {
          parameters: [metabaseDateRangeParam(dateRange), ...metabaseBrandNameParams(brands)],
        },
      }),
      transformResponse: safeParse<MtoMerchHeadBrandContribution>(
        MtoMerchHeadBrandContributionSchema,
        'mtoMerchHeadBrandContribution',
      ),
      keepUnusedDataFor: 600,
    }),
    mtoMerchHeadIssueBreakdown: builder.query<
      MtoMerchHeadIssueBreakdown,
      { dateRange: string; brands?: string[] }
    >({
      query: ({ dateRange, brands = [] }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.mtoMerchHeadIssueBreakdown}/query/json`,
        body: {
          parameters: [metabaseDateRangeParam(dateRange), ...metabaseBrandNameParams(brands)],
        },
      }),
      transformResponse: safeParse<MtoMerchHeadIssueBreakdown>(
        MtoMerchHeadIssueBreakdownSchema,
        'mtoMerchHeadIssueBreakdown',
      ),
      keepUnusedDataFor: 600,
    }),
    mtoMerchHeadRawIssueLines: builder.query<
      MtoMerchHeadRawIssueLines,
      { dateRange: string; brands?: string[] }
    >({
      query: ({ dateRange, brands = [] }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.mtoMerchHeadRawIssueLines}/query/json`,
        body: {
          parameters: [metabaseDateRangeParam(dateRange), ...metabaseBrandNameParams(brands)],
        },
      }),
      transformResponse: safeParse<MtoMerchHeadRawIssueLines>(
        MtoMerchHeadRawIssueLinesSchema,
        'mtoMerchHeadRawIssueLines',
      ),
      keepUnusedDataFor: 600,
    }),
    jitB2B2CVendorPackingSlaSummary: builder.query<
      JitB2B2CVendorPackingSlaSummary,
      { dateRange: string; brand?: string }
    >({
      query: ({ dateRange, brand }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.jitB2B2CVendorPackingSlaSummary}/query/json`,
        body: {
          parameters: [
            metabaseDateRangeParam(dateRange),
            ...(brand ? [metabaseTextParam('brand_name', brand)] : []),
          ],
        },
      }),
      transformResponse: safeParse<JitB2B2CVendorPackingSlaSummary>(
        JitB2B2CVendorPackingSlaSummarySchema,
        'jitB2B2CVendorPackingSlaSummary',
      ),
      keepUnusedDataFor: 600,
    }),
    jitB2B2CVendorPackingSlaWeekly: builder.query<
      JitB2B2CVendorPackingSlaWeekly,
      { dateRange: string; brand?: string }
    >({
      query: ({ dateRange, brand }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.jitB2B2CVendorPackingSlaWeekly}/query/json`,
        body: {
          parameters: [
            metabaseDateRangeParam(dateRange),
            ...(brand ? [metabaseTextParam('brand_name', brand)] : []),
          ],
        },
      }),
      transformResponse: safeParse<JitB2B2CVendorPackingSlaWeekly>(
        JitB2B2CVendorPackingSlaWeeklySchema,
        'jitB2B2CVendorPackingSlaWeekly',
      ),
      keepUnusedDataFor: 600,
    }),
    jitB2B2CVendorPackingSlaByBrand: builder.query<
      JitB2B2CVendorPackingSlaByBrand,
      { dateRange: string; brand?: string }
    >({
      query: ({ dateRange, brand }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.jitB2B2CVendorPackingSlaByBrand}/query/json`,
        body: {
          parameters: [
            metabaseDateRangeParam(dateRange),
            ...(brand ? [metabaseTextParam('brand_name', brand)] : []),
          ],
        },
      }),
      transformResponse: safeParse<JitB2B2CVendorPackingSlaByBrand>(
        JitB2B2CVendorPackingSlaByBrandSchema,
        'jitB2B2CVendorPackingSlaByBrand',
      ),
      keepUnusedDataFor: 600,
    }),
    jitB2B2CVendorPackingSlaRaw: builder.query<
      JitB2B2CVendorPackingSlaRaw,
      { dateRange: string; brand?: string }
    >({
      query: ({ dateRange, brand }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.jitB2B2CVendorPackingSlaRaw}/query/json`,
        body: {
          parameters: [
            metabaseDateRangeParam(dateRange),
            ...(brand ? [metabaseTextParam('brand_name', brand)] : []),
          ],
        },
      }),
      transformResponse: safeParse<JitB2B2CVendorPackingSlaRaw>(
        JitB2B2CVendorPackingSlaRawSchema,
        'jitB2B2CVendorPackingSlaRaw',
      ),
      keepUnusedDataFor: 600,
    }),
    catalogCreationWeeklySummary: builder.query<
      CatalogCreation,
      { dateStart: string; dateEnd: string }
    >({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.catalogCreationWeeklySummary}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
          ],
        },
      }),
      transformResponse: safeParse<CatalogCreation>(
        CatalogCreationSchema,
        'catalogCreationWeeklySummary',
      ),
      keepUnusedDataFor: 600,
    }),
    tagDetailItems: builder.query<
      TagDetailItems,
      { aiTag: string; dateStart: string; dateEnd: string }
    >({
      query: ({ aiTag, dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.tagDetailItems}/query/json`,
        body: {
          parameters: [
            { type: 'category', value: aiTag, target: ['variable', ['template-tag', 'ai_tag']] },
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
          ],
        },
      }),
      transformResponse: safeParse<TagDetailItems>(TagDetailItemsSchema, 'tagDetailItems'),
      keepUnusedDataFor: 600,
    }),
    catalogQualityBrandSummary: builder.query<
      CatalogQuality,
      { dateStart: string; dateEnd: string }
    >({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.catalogQualityBrandSummary}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
          ],
        },
      }),
      transformResponse: safeParse<CatalogQuality>(
        CatalogQualitySchema,
        'catalogQualityBrandSummary',
      ),
      keepUnusedDataFor: 600,
    }),
    catalogStylesHealth: builder.query<CatalogStylesHealth, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.catalogStylesHealth}/query/json`,
      }),
      transformResponse: safeParse<CatalogStylesHealth>(
        CatalogStylesHealthSchema,
        'catalogStylesHealth',
      ),
      keepUnusedDataFor: 600,
    }),
    stockAgeingBrandBucket: builder.query<StockAgeingBrandBucket, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.stockAgeingBrandBucket}/query/json`,
      }),
      transformResponse: safeParse<StockAgeingBrandBucket>(
        StockAgeingBrandBucketSchema,
        'stockAgeingBrandBucket',
      ),
      keepUnusedDataFor: 600,
    }),
    stockAgeingWritedown: builder.query<StockAgeingWritedown, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.stockAgeingWritedown}/query/json`,
      }),
      transformResponse: safeParse<StockAgeingWritedown>(
        StockAgeingWritedownSchema,
        'stockAgeingWritedown',
      ),
      keepUnusedDataFor: 600,
    }),
    stockAgeingFacilityBucket: builder.query<StockAgeingFacilityBucket, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.stockAgeingFacilityBucket}/query/json`,
      }),
      transformResponse: safeParse<StockAgeingFacilityBucket>(
        StockAgeingFacilityBucketSchema,
        'stockAgeingFacilityBucket',
      ),
      keepUnusedDataFor: 600,
    }),
    onlineTrafficBrandSource: builder.query<
      OnlineTrafficBrandSource,
      { dateStart: string; dateEnd: string }
    >({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.onlineTrafficBrandSource}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
          ],
        },
      }),
      transformResponse: safeParse<OnlineTrafficBrandSource>(
        OnlineTrafficBrandSourceSchema,
        'onlineTrafficBrandSource',
      ),
      keepUnusedDataFor: 600,
    }),
    onlineTopTrafficStyles: builder.query<
      OnlineTopTrafficStyles,
      { dateStart: string; dateEnd: string }
    >({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.onlineTopTrafficStyles}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
          ],
        },
      }),
      transformResponse: safeParse<OnlineTopTrafficStyles>(
        OnlineTopTrafficStylesSchema,
        'onlineTopTrafficStyles',
      ),
      keepUnusedDataFor: 600,
    }),
    onlineAddToCarts: builder.query<OnlineAddToCarts, { dateStart: string; dateEnd: string }>({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.onlineAddToCarts}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
          ],
        },
      }),
      transformResponse: safeParse<OnlineAddToCarts>(OnlineAddToCartsSchema, 'onlineAddToCarts'),
      keepUnusedDataFor: 600,
    }),
    onlineLiveStyles: builder.query<OnlineLiveStyles, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.onlineLiveStyles}/query/json`,
      }),
      transformResponse: safeParse<OnlineLiveStyles>(OnlineLiveStylesSchema, 'onlineLiveStyles'),
      keepUnusedDataFor: 600,
    }),
    onlinePerformanceDaily: builder.query<
      OnlinePerformanceDaily,
      { dateStart: string; dateEnd: string }
    >({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.onlinePerformanceDaily}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
          ],
        },
      }),
      transformResponse: safeParse<OnlinePerformanceDaily>(
        OnlinePerformanceDailySchema,
        'onlinePerformanceDaily',
      ),
      keepUnusedDataFor: 600,
    }),
    onlinePerformanceMerchDashMix: builder.query<
      OnlinePerformanceMerchDashMix,
      { dateStart: string; dateEnd: string }
    >({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.onlinePerformanceMerchDashMix}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
          ],
        },
      }),
      transformResponse: safeParse<OnlinePerformanceMerchDashMix>(
        OnlinePerformanceMerchDashMixSchema,
        'onlinePerformanceMerchDashMix',
      ),
      keepUnusedDataFor: 600,
    }),
    onlinePerformanceStyleLevel: builder.query<
      OnlinePerformanceStyleLevel,
      {
        dateStart: string;
        dateEnd: string;
        /* Empty means every brand. Sent server-side rather than filtered in the
           browser: the unfiltered catalogue is ~75k style rows. */
        brands?: Array<string>;
        /* Server-side for the same reason as brands, and because this card is
           paginated: a browser-side filter would only narrow the 25 rows of the
           current page, leaving the pager and totals describing a wider set. */
        brandManagers?: Array<string>;
        merchPortfolios?: Array<string>;
        sortKey?: string;
        sortDir?: 'asc' | 'desc';
        limit?: number;
        offset?: number;
      }
    >({
      query: ({
        dateStart,
        dateEnd,
        brands = [],
        brandManagers = [],
        merchPortfolios = [],
        sortKey = 'ordered_items',
        sortDir = 'desc',
        limit = 25,
        offset = 0,
      }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.onlinePerformanceStyleLevel}/query/json`,
        body: {
          parameters: [
            /* date/single, because this card's date tags are declared `date`.
               The sibling brand card 8129 sends `category` for its dates — its
               tags are text — so the two differ deliberately. Do not "fix" this
               to match 8129: verified binding here by querying one day vs 26
               and getting different top figures. Both tags are optional, so a
               mismatch would not error, it would silently widen the window. */
            { type: 'date/single', value: dateStart, target: ['variable', ['template-tag', 'date_start']] },
            { type: 'date/single', value: dateEnd, target: ['variable', ['template-tag', 'date_end']] },
            /* Comma-joined: the tag is text and the card splits it. An empty
               string is the card's "all brands" case. */
            { type: 'category', value: brands.join(','), target: ['variable', ['template-tag', 'brands']] },
            /* Same comma-joined shape, and the same empty-string "all" case. */
            { type: 'category', value: brandManagers.join(','), target: ['variable', ['template-tag', 'brand_managers']] },
            { type: 'category', value: merchPortfolios.join(','), target: ['variable', ['template-tag', 'merch_portfolios']] },
            { type: 'category', value: sortKey, target: ['variable', ['template-tag', 'sort_key']] },
            { type: 'category', value: sortDir, target: ['variable', ['template-tag', 'sort_dir']] },
            /* Numeric tags take their value as an array, matching the other
               number/= parameters in this file. */
            { type: 'number/=', value: [limit], target: ['variable', ['template-tag', 'row_limit']] },
            { type: 'number/=', value: [offset], target: ['variable', ['template-tag', 'row_offset']] },
          ],
        },
      }),
      transformResponse: safeParse<OnlinePerformanceStyleLevel>(
        OnlinePerformanceStyleLevelSchema,
        'onlinePerformanceStyleLevel',
      ),
      keepUnusedDataFor: 600,
    }),
    stockReconciliationFacilitySummary: builder.query<StockReconciliationFacility, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.stockReconciliationFacilitySummary}/query/json`,
      }),
      transformResponse: safeParse<StockReconciliationFacility>(
        StockReconciliationFacilitySchema,
        'stockReconciliationFacilitySummary',
      ),
      keepUnusedDataFor: 600,
    }),
    monthlyMerchKpiRevenue: builder.query<MonthlyKpiRevenue, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.monthlyMerchKpiRevenue}/query/json`,
      }),
      transformResponse: safeParse<MonthlyKpiRevenue>(
        MonthlyKpiRevenueSchema,
        'monthlyMerchKpiRevenue',
      ),
      keepUnusedDataFor: 600,
    }),
    monthlyMerchKpiSizeAvailability: builder.query<MonthlyKpiSizeAvailability, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.monthlyMerchKpiSizeAvailability}/query/json`,
      }),
      transformResponse: safeParse<MonthlyKpiSizeAvailability>(
        MonthlyKpiSizeAvailabilitySchema,
        'monthlyMerchKpiSizeAvailability',
      ),
      keepUnusedDataFor: 600,
    }),
    monthlyMerchKpiCx: builder.query<MonthlyKpiCx, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.monthlyMerchKpiCx}/query/json`,
      }),
      transformResponse: safeParse<MonthlyKpiCx>(MonthlyKpiCxSchema, 'monthlyMerchKpiCx'),
      keepUnusedDataFor: 600,
    }),
    depthBreadthTargets: builder.query<DepthBreadthTargets, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.depthBreadthTargets}/query/json`,
      }),
      transformResponse: safeParse<DepthBreadthTargets>(
        DepthBreadthTargetsSchema,
        'depthBreadthTargets',
      ),
      keepUnusedDataFor: 600,
    }),
    depthBreadthDaily: builder.query<
      DepthBreadthDaily,
      { facility: string; dateStart: string; dateEnd: string }
    >({
      query: ({ facility, dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.depthBreadthDaily}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: facility,
              target: ['variable', ['template-tag', 'facility']],
            },
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
          ],
        },
      }),
      transformResponse: safeParse<DepthBreadthDaily>(DepthBreadthDailySchema, 'depthBreadthDaily'),
      keepUnusedDataFor: 600,
    }),
    depthBreadthLive: builder.query<DepthBreadthLive, { facility: string }>({
      query: ({ facility }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.depthBreadthLive}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: facility,
              target: ['variable', ['template-tag', 'facility']],
            },
          ],
        },
      }),
      transformResponse: safeParse<DepthBreadthLive>(DepthBreadthLiveSchema, 'depthBreadthLive'),
      keepUnusedDataFor: 60,
    }),
    depthBreadthBrandOptions: builder.query<DepthBreadthBrandOptions, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.depthBreadthBrandOptions}/query/json`,
      }),
      transformResponse: safeParse<DepthBreadthBrandOptions>(
        DepthBreadthBrandOptionsSchema,
        'depthBreadthBrandOptions',
      ),
      keepUnusedDataFor: 600,
    }),
    monthlyMerchKpiBrandManagerTargets: builder.query<MonthlyKpiBrandManagerTarget, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.monthlyMerchKpiBrandManagerTargets}/query/json`,
      }),
      transformResponse: safeParse<MonthlyKpiBrandManagerTarget>(
        MonthlyKpiBrandManagerTargetSchema,
        'monthlyMerchKpiBrandManagerTargets',
      ),
      keepUnusedDataFor: 600,
    }),
    stylistLeaderboard: builder.query<StylistLeaderboard, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.stylistLeaderboard}/query/json`,
      }),
      transformResponse: safeParse<StylistLeaderboard>(
        StylistLeaderboardSchema,
        'stylistLeaderboard',
      ),
      keepUnusedDataFor: 600,
    }),
    auditHistory: builder.query<AuditHistory, { entityKind: string; entityId: string }>({
      query: ({ entityKind, entityId }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.auditHistory}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: entityKind,
              target: ['variable', ['template-tag', 'entity_kind']],
            },
            {
              type: 'category',
              value: entityId,
              target: ['variable', ['template-tag', 'entity_id']],
            },
          ],
        },
      }),
      transformResponse: safeParse<AuditHistory>(AuditHistorySchema, 'auditHistory'),
      keepUnusedDataFor: 60,
    }),
    inventoryShelves: builder.query<InventoryShelves, { facility: string; skuCode: string }>({
      query: ({ facility, skuCode }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.inventoryShelves}/query/json`,
        body: {
          parameters: [
            {
              id: 'tag-facility',
              type: 'category',
              value: facility,
              target: ['variable', ['template-tag', 'facility']],
            },
            {
              id: 'tag-sku-code',
              type: 'category',
              value: skuCode,
              target: ['variable', ['template-tag', 'sku_code']],
            },
          ],
        },
      }),
      transformResponse: safeParse<InventoryShelves>(InventoryShelvesSchema, 'inventoryShelves'),
      keepUnusedDataFor: 60,
    }),
    inventoryStockHistory: builder.query<InventoryStockHistory, { inventoryId: number }>({
      query: ({ inventoryId }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.inventoryStockHistory}/query/json`,
        body: {
          parameters: [
            {
              id: 'tag-inventory-id',
              type: 'category',
              value: String(inventoryId),
              target: ['variable', ['template-tag', 'inventory_id']],
            },
          ],
        },
      }),
      transformResponse: safeParse<InventoryStockHistory>(
        InventoryStockHistorySchema,
        'inventoryStockHistory',
      ),
      keepUnusedDataFor: 60,
    }),
    inventoryCartBlocks: builder.query<InventoryCartBlocks, { cartItemIds: Array<number> }>({
      query: ({ cartItemIds }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.inventoryCartBlocks}/query/json`,
        body: {
          parameters: [
            {
              id: 'tag-cart-item-ids',
              type: 'category',
              value: cartItemIds.join(','),
              target: ['variable', ['template-tag', 'cart_item_ids']],
            },
          ],
        },
      }),
      transformResponse: safeParse<InventoryCartBlocks>(InventoryCartBlocksSchema, 'inventoryCartBlocks'),
      keepUnusedDataFor: 60,
    }),
    brandLeadInfo: builder.query<Array<{ brand_manager: string }>, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.brandLeadInfo}/query/json`,
      }),
      transformResponse: safeParse<Array<{ brand_manager: string }>>(
        z.array(z.object({ brand_manager: z.string() })),
        'brandLeadInfo',
      ),
      keepUnusedDataFor: 3600,
    }),
    /* Operations pages (2026-07-23) — Movements + Stock views. */
    opsInwardsDcSummary: builder.query<OpsInwardsDc, { dateStart: string; dateEnd: string }>({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.opsInwardsDcSummary}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
          ],
        },
      }),
      transformResponse: safeParse<OpsInwardsDc>(OpsInwardsDcSchema, 'opsInwardsDcSummary'),
      keepUnusedDataFor: 600,
    }),
    opsGpoPerformance: builder.query<OpsGpo, { dateStart: string; dateEnd: string }>({
      query: ({ dateStart, dateEnd }) => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.opsGpoPerformance}/query/json`,
        body: {
          parameters: [
            {
              type: 'category',
              value: dateStart,
              target: ['variable', ['template-tag', 'date_start']],
            },
            {
              type: 'category',
              value: dateEnd,
              target: ['variable', ['template-tag', 'date_end']],
            },
          ],
        },
      }),
      transformResponse: safeParse<OpsGpo>(OpsGpoSchema, 'opsGpoPerformance'),
      keepUnusedDataFor: 600,
    }),
    opsStockByLocation: builder.query<OpsStockLocation, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.opsStockByLocation}/query/json`,
      }),
      transformResponse: safeParse<OpsStockLocation>(OpsStockLocationSchema, 'opsStockByLocation'),
      keepUnusedDataFor: 600,
    }),
    opsStockLocationExtras: builder.query<OpsStockLocationExtras, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.opsStockLocationExtras}/query/json`,
      }),
      transformResponse: safeParse<OpsStockLocationExtras>(
        OpsStockLocationExtrasSchema,
        'opsStockLocationExtras',
      ),
      keepUnusedDataFor: 600,
    }),
    opsActionMatrix: builder.query<OpsActionMatrix, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.opsActionMatrix}/query/json`,
      }),
      transformResponse: safeParse<OpsActionMatrix>(OpsActionMatrixSchema, 'opsActionMatrix'),
      keepUnusedDataFor: 600,
    }),
    opsSkuExport: builder.query<OpsSkuExport, void>({
      query: () => ({ method: 'POST', url: `/cube/card/${CUBE_CARD_IDS.opsSkuExport}/query/json` }),
      transformResponse: safeParse<OpsSkuExport>(OpsSkuExportSchema, 'opsSkuExport'),
      keepUnusedDataFor: 600,
    }),
    opsStockAgeing: builder.query<OpsStockAgeing, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.opsStockAgeing}/query/json`,
      }),
      transformResponse: safeParse<OpsStockAgeing>(OpsStockAgeingSchema, 'opsStockAgeing'),
      keepUnusedDataFor: 600,
    }),
    opsStockVelocity: builder.query<OpsStockVelocity, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.opsStockVelocity}/query/json`,
      }),
      transformResponse: safeParse<OpsStockVelocity>(OpsStockVelocitySchema, 'opsStockVelocity'),
      keepUnusedDataFor: 600,
    }),
    opsPendingDcs: builder.query<OpsPendingDc, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.opsPendingDcs}/query/json`,
      }),
      transformResponse: safeParse<OpsPendingDc>(OpsPendingDcSchema, 'opsPendingDcs'),
      keepUnusedDataFor: 600,
    }),
    opsDcTimeline: builder.query<OpsDcTimeline, number>({
      query: dcId => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.opsDcTimeline}/query/json`,
        body: {
          parameters: [
            {
              type: 'number/=',
              value: [dcId],
              target: ['variable', ['template-tag', 'dc_id']],
            },
          ],
        },
      }),
      transformResponse: safeParse<OpsDcTimeline>(OpsDcTimelineSchema, 'opsDcTimeline'),
      keepUnusedDataFor: 300,
    }),
    opsGpoGatepassTimeline: builder.query<OpsGpoGatepassTimeline, number>({
      query: gpoId => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.opsGpoGatepassTimeline}/query/json`,
        body: {
          parameters: [
            {
              type: 'number/=',
              value: [gpoId],
              target: ['variable', ['template-tag', 'gpo_id']],
            },
          ],
        },
      }),
      transformResponse: safeParse<OpsGpoGatepassTimeline>(
        OpsGpoGatepassTimelineSchema,
        'opsGpoGatepassTimeline',
      ),
      keepUnusedDataFor: 300,
    }),
    opsStockRecon: builder.query<OpsStockRecon, void>({
      query: () => ({
        method: 'POST',
        url: `/cube/card/${CUBE_CARD_IDS.opsStockRecon}/query/json`,
      }),
      transformResponse: safeParse<OpsStockRecon>(OpsStockReconSchema, 'opsStockRecon'),
      keepUnusedDataFor: 600,
    }),
  }),
});

export const {
  useOpenWorkMapWorkQuery,
  useOpenWorkMapActiveRidersQuery,
  useOpenWorkMapActiveFacilitiesQuery,
  useOpenWorkMapJitExpressFacilitiesQuery,
  useRiderDayEvidenceQuery,
  useRiderDayShiftContextQuery,
  useRiderDayDistanceEvidenceQuery,
  useRiderDayTripsQuery,
  useRiderFailedAttempts7dQuery,
  useRiderDayFailedAttemptsQuery,
  useLazyHubStoreImageQueueQuery,
  useRunReportQueryQuery,
  useLazyRunReportQueryQuery,
  useLazyRunCardQuery,
  useBrandWiseMTOPendencyQuery,
  useMtoHomepageOneViewQuery,
  useInwardTrackerBrandSummaryQuery,
  useLazyInwardTrackerItemsQuery,
  useLazyInwardTrackerStoreBreakdownQuery,
  useSaleStockBrandSummaryQuery,
  useStoreBrandSummaryQuery,
  useBrandMtdNetGmvQuery,
  useAllStylesSoldStoreSplitQuery,
  useBestsellersTopStylesQuery,
  useBestsellersSizeDemandQuery,
  useLazyBestsellersSizeDemandQuery,
  useAiTagWeeklySummaryQuery,
  useCoyuSearchesTopQueriesQuery,
  useStockTransferBrandSummaryQuery,
  useStockTransferGpoListQuery,
  useLazyStockTransferGpoListQuery,
  useMtoOrdersSummaryQuery,
  useMtoLiveOrdersQuery,
  useMtoWeeklyTrendQuery,
  useMtoDelayHistogramQuery,
  useLazyMtoDelayDetailOrdersQuery,
  useLazyMtoLiveOrdersDetailQuery,
  useMtoMerchHeadWeeklyLeaderboardQuery,
  useMtoMerchHeadSelectedWindowComparisonQuery,
  useMtoMerchPortfolioSelectedWindowComparisonQuery,
  useMtoMerchHeadWeeklyTrendQuery,
  useMtoMerchHeadBrandContributionQuery,
  useMtoMerchHeadIssueBreakdownQuery,
  useLazyMtoMerchHeadRawIssueLinesQuery,
  useJitB2B2CVendorPackingSlaSummaryQuery,
  useJitB2B2CVendorPackingSlaWeeklyQuery,
  useJitB2B2CVendorPackingSlaByBrandQuery,
  useJitB2B2CVendorPackingSlaRawQuery,
  useLazyJitB2B2CVendorPackingSlaRawQuery,
  useCatalogCreationWeeklySummaryQuery,
  useCatalogQualityBrandSummaryQuery,
  useCatalogStylesHealthQuery,
  useLazyTagDetailItemsQuery,
  useStockAgeingBrandBucketQuery,
  useLazyStockAgeingWritedownQuery,
  useStockAgeingFacilityBucketQuery,
  useOnlinePerformanceDailyQuery,
  useOnlinePerformanceStyleLevelQuery,
  useLazyOnlinePerformanceStyleLevelQuery,
  useOnlinePerformanceMerchDashMixQuery,
  useOnlineTrafficBrandSourceQuery,
  useOnlineTopTrafficStylesQuery,
  useOnlineLiveStylesQuery,
  useOnlineAddToCartsQuery,
  useStockReconciliationFacilitySummaryQuery,
  useMonthlyMerchKpiRevenueQuery,
  useMonthlyMerchKpiSizeAvailabilityQuery,
  useMonthlyMerchKpiCxQuery,
  useMonthlyMerchKpiBrandManagerTargetsQuery,
  useDepthBreadthTargetsQuery,
  useDepthBreadthDailyQuery,
  useDepthBreadthLiveQuery,
  useDepthBreadthBrandOptionsQuery,
  useLazyAuditHistoryQuery,
  useInventoryShelvesQuery,
  useInventoryStockHistoryQuery,
  useInventoryCartBlocksQuery,
  useStylistLeaderboardQuery,
  useBrandLeadInfoQuery,
  useOpsInwardsDcSummaryQuery,
  useOpsGpoPerformanceQuery,
  useOpsStockByLocationQuery,
  useOpsStockAgeingQuery,
  useOpsStockVelocityQuery,
  useOpsStockReconQuery,
  useOpsPendingDcsQuery,
  useOpsDcTimelineQuery,
  useOpsGpoGatepassTimelineQuery,
  useOpsStockLocationExtrasQuery,
  useOpsActionMatrixQuery,
  useLazyOpsSkuExportQuery,
} = metabaseApi;
